import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, LogOut, ArrowLeft, FileText, Megaphone } from "lucide-react";
import { api } from "@/lib/api";
import { readToken, clearToken } from "@/lib/tokenStore";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

const TYPE_TABS = [
  { value: "blog", label: "Blogbeiträge", icon: FileText },
  { value: "ausschreibung", label: "Ausschreibungen", icon: Megaphone },
];

export default function AdminContent() {
  const navigate = useNavigate();
  const [type, setType] = useState("blog");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const token = readToken();

  useEffect(() => {
    if (!token) {
      navigate("/admin");
      return;
    }
    let cancelled = false;
    setLoading(true);
    api
      .get(`/admin/content?type=${type}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (!cancelled) setItems(r.data.items || []);
      })
      .catch((e) => {
        if (e?.response?.status === 401) {
          clearToken();
          navigate("/admin");
        } else {
          toast.error("Laden fehlgeschlagen");
        }
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [type, token, navigate]);

  const logout = async () => {
    try {
      await api.post("/admin/logout", {}, { headers: { Authorization: `Bearer ${token}` } });
    } catch {}
    clearToken();
    navigate("/admin");
  };

  const remove = async (id) => {
    if (!window.confirm("Wirklich löschen?")) return;
    try {
      await api.delete(`/admin/content/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setItems((prev) => prev.filter((x) => x.id !== id));
      toast.success("Gelöscht");
    } catch {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  return (
    <div className="min-h-screen bg-[#050607] text-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <Link
              to="/admin"
              className="overline inline-flex items-center gap-2 hover:text-white transition-colors"
            >
              <ArrowLeft size={14} strokeWidth={1.5} /> Admin
            </Link>
            <h1
              className="font-display text-3xl md:text-4xl uppercase mt-3"
              data-testid="admin-content-title"
            >
              Content Editor
            </h1>
          </div>
          <button onClick={logout} className="btn-secondary" data-testid="admin-content-logout">
            <LogOut size={14} strokeWidth={1.5} /> Logout
          </button>
        </div>

        {/* Tabs */}
        <div className="mt-10 border-b border-[rgba(255,255,255,0.08)] flex gap-2">
          {TYPE_TABS.map((t) => {
            const Icon = t.icon;
            const active = type === t.value;
            return (
              <button
                key={t.value}
                onClick={() => setType(t.value)}
                data-testid={`admin-tab-${t.value}`}
                className={`flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-[0.18em] border-b-2 transition-colors ${
                  active
                    ? "border-[#18E096] text-white"
                    : "border-transparent text-[#AEB7B7] hover:text-white"
                }`}
              >
                <Icon size={14} strokeWidth={1.5} /> {t.label}
              </button>
            );
          })}
        </div>

        {/* Toolbar */}
        <div className="mt-8 flex flex-wrap justify-between gap-4 items-center">
          <p className="text-[#AEB7B7] text-sm">
            {items.length} {items.length === 1 ? "Eintrag" : "Einträge"}
          </p>
          <Link
            to={
              type === "blog"
                ? "/admin/content/blog/new"
                : "/admin/content/ausschreibungen/new"
            }
            className="btn-primary"
            data-testid={`admin-new-${type}`}
          >
            <Plus size={14} strokeWidth={1.5} /> Neuer Eintrag
          </Link>
        </div>

        {/* List */}
        <div className="mt-8 surface overflow-x-auto">
          {loading ? (
            <p className="p-6 text-[#AEB7B7]">Lädt…</p>
          ) : items.length === 0 ? (
            <p className="p-6 text-[#AEB7B7]">Noch keine Einträge.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[#0B0F10] text-[#AEB7B7] uppercase text-xs tracking-[0.16em]">
                <tr>
                  <th className="text-left p-4">Titel</th>
                  <th className="text-left p-4">Slug</th>
                  <th className="text-left p-4">Status</th>
                  <th className="text-left p-4">Veröffentlicht</th>
                  <th className="text-right p-4">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr
                    key={it.id}
                    className="border-t border-[rgba(255,255,255,0.06)] hover:bg-[#0B0F10] transition-colors"
                    data-testid={`admin-row-${it.id}`}
                  >
                    <td className="p-4">
                      <div className="font-medium">{it.title}</div>
                      <div className="text-[#AEB7B7] text-xs">
                        {it.category || it.posting_type || "—"}
                      </div>
                    </td>
                    <td className="p-4 text-[#AEB7B7] font-mono text-xs">{it.slug}</td>
                    <td className="p-4">
                      <span
                        className={`px-2 py-1 text-xs ${
                          it.status === "published"
                            ? "bg-[#18E096]/15 text-[#18E096]"
                            : "bg-[#AEB7B7]/15 text-[#AEB7B7]"
                        }`}
                      >
                        {it.status === "published" ? "Veröffentlicht" : "Entwurf"}
                      </span>
                    </td>
                    <td className="p-4 text-[#AEB7B7]">{formatDate(it.published_at)}</td>
                    <td className="p-4">
                      <div className="flex justify-end gap-2">
                        <Link
                          to={`/admin/content/${
                            it.type === "blog" ? "blog" : "ausschreibungen"
                          }/${it.id}`}
                          className="btn-secondary text-xs"
                          data-testid={`admin-edit-${it.id}`}
                        >
                          <Edit2 size={12} strokeWidth={1.5} /> Bearbeiten
                        </Link>
                        <button
                          onClick={() => remove(it.id)}
                          className="btn-secondary text-xs"
                          data-testid={`admin-delete-${it.id}`}
                        >
                          <Trash2 size={12} strokeWidth={1.5} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
